import api from './api';

/**
 * Admin-Service: Push-Kampagnen (Portierung des Flutter-Admin-Panels
 * admin_push_campaigns_screen.dart).
 *
 * Backend: /api/push-campaigns* (requiresAuth + onlyAdmin, dc-sign via api-Client)
 * User-Liste fürs Targeting: /api/admin/users (targetPlayerIds = notificationId
 * = OneSignal-Player-ID; nur User MIT notificationId sind pushbar).
 */

export type PushCampaignStatus = 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';

export interface PushCampaignResult {
  recipientCount?: number | null;
  successCount?: number | null;
  // Backend nennt es failedCount – ältere Clients failureCount
  failedCount?: number | null;
  failureCount?: number | null;
  oneSignalId?: string | null;
  errorMessage?: string | null;
  error?: string | null;
  sentAt?: string | null;
}

export interface PushCampaign {
  _id?: string | null;
  id?: string | null;
  title?: string | null;
  message?: string | null;
  scheduledAt?: string | null;
  targetType?: 'all' | 'selected' | string | null;
  targetPlayerIds?: string[] | null;
  status?: PushCampaignStatus | string | null;
  result?: PushCampaignResult | null;
  data?: Record<string, unknown> | null;
  sentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PushCampaignStats {
  total?: number | null;
  scheduled?: number | null;
  sent?: number | null;
  failed?: number | null;
  cancelled?: number | null;
}

export interface AdminPushUser {
  _id?: string | null;
  id?: string | null;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  image?: string | null;
  notificationId?: string | null;
}

export interface PushCampaignPayload {
  title: string;
  message: string;
  scheduledAt: string; // ISO8601
  targetType: 'all' | 'selected';
  targetPlayerIds: string[];
  data?: Record<string, unknown>;
}

class AdminPushCampaignsService {
  // ---------- Kampagnen ----------

  async listCampaigns(limit = 100, skip = 0): Promise<{ campaigns: PushCampaign[]; total: number }> {
    const response = await api.get('/api/push-campaigns', { params: { limit, skip } });
    return {
      campaigns: (response.data?.campaigns as PushCampaign[]) ?? [],
      total: response.data?.total ?? 0,
    };
  }

  async getStats(): Promise<PushCampaignStats> {
    const response = await api.get('/api/push-campaigns/stats');
    return (response.data as PushCampaignStats) ?? {};
  }

  async createCampaign(payload: PushCampaignPayload): Promise<PushCampaign> {
    const response = await api.post('/api/push-campaigns', payload);
    return response.data as PushCampaign;
  }

  async updateCampaign(id: string, payload: Partial<PushCampaignPayload>): Promise<PushCampaign> {
    const response = await api.put(`/api/push-campaigns/${id}`, payload);
    return response.data as PushCampaign;
  }

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/api/push-campaigns/${id}`);
  }

  async cancelCampaign(id: string): Promise<PushCampaign> {
    const response = await api.post(`/api/push-campaigns/${id}/cancel`);
    return response.data as PushCampaign;
  }

  async sendCampaignNow(id: string): Promise<PushCampaign> {
    const response = await api.post(`/api/push-campaigns/${id}/send`);
    return response.data as PushCampaign;
  }

  // ---------- User fürs Targeting ----------

  /** Alle User laden; nur die MIT notificationId sind als Push-Ziel wählbar. */
  async listPushableUsers(): Promise<AdminPushUser[]> {
    const response = await api.get('/api/admin/users');
    const users = Array.isArray(response.data) ? (response.data as AdminPushUser[]) : [];
    return users.filter((u) => u.notificationId && String(u.notificationId).trim().length > 0);
  }

  // ---------- Helper ----------

  /** Vereinheitlicht das result-Feld (failedCount/failureCount, errorMessage/error). */
  normalizeResult(campaign: PushCampaign): {
    recipientCount: number;
    successCount: number;
    failedCount: number;
    sentAt: string | null;
    error: string | null;
  } | null {
    const r = campaign.result;
    if (!r) return null;
    return {
      recipientCount: r.recipientCount ?? 0,
      successCount: r.successCount ?? 0,
      failedCount: r.failedCount ?? r.failureCount ?? 0,
      sentAt: campaign.sentAt ?? r.sentAt ?? null,
      error: r.errorMessage ?? r.error ?? null,
    };
  }

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: unknown } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') {
      const msg = (data as { message?: string; error?: string }).message
        ?? (data as { message?: string; error?: string }).error;
      if (msg) return msg;
    }
    return fallback;
  }
}

const adminPushCampaignsService = new AdminPushCampaignsService();
export default adminPushCampaignsService;
